import type { AnalyticsEventPayload } from '@atlaskit/analytics-next';

import { filterCommonAttributes } from '../../../ai-answer-dialog/analytics-helpers';
import type { commonAttributesType } from '../../../ai-answer-dialog/types';
import { logger } from '../../../common/utils/logger';

type OnLinkClickParams = {
	query: string;
	queryHash: string;
	url: string;
	bookmarkId: string;
};

// When the user clicks the link button
export const onLinkClick: (props: OnLinkClickParams) => AnalyticsEventPayload = (
	props: OnLinkClickParams,
): AnalyticsEventPayload => {
	try {
		const { query, queryHash, url, bookmarkId } = props;
		return {
			action: 'clicked',
			actionSubject: 'aiSearchBookmarkLink',
			eventType: 'ui',
			attributes: {
				queryHash,
				bookmarkId,
			},
			nonPrivacySafeAttributes: {
				query,
				url,
			},
		};
	} catch (error) {
		logger.error({ error, functionName: 'onLinkClick' });
		return {};
	}
};

type OnBookmarkShownParams = {
	query: string;
	queryHash: string;
	url: string;
	bookmarkId: string;
};

// When the user clicks the link button
export const onBookmarkShown: (props: OnBookmarkShownParams) => AnalyticsEventPayload = (
	props: OnBookmarkShownParams,
): AnalyticsEventPayload => {
	try {
		const { query, queryHash, url, bookmarkId } = props;
		return {
			action: 'shown',
			actionSubject: 'aiSearchBookmark',
			eventType: 'track',
			attributes: {
				queryHash,
				bookmarkId,
			},
			nonPrivacySafeAttributes: {
				query,
				url,
			},
		};
	} catch (error) {
		logger.error({ error, functionName: 'onBookmarkShown' });
		return {};
	}
};

// When an admin types in a query but no bookmark exists and is shown the add bookmark component (different than modal)
export const onAddBookmarkShown: (props: { query: string }) => AnalyticsEventPayload = (props: {
	query: string;
}): AnalyticsEventPayload => {
	try {
		const { query } = props;
		return {
			action: 'shown',
			actionSubject: 'aiSearchAddBookmark',
			eventType: 'track',
			nonPrivacySafeAttributes: {
				query,
			},
		};
	} catch (error) {
		logger.error({ error, functionName: 'onAddBookmarkShown' });
		return {};
	}
};

export const onAddPinModalOpen: (
	isUpdateModal: boolean,
	source?: string,
) => AnalyticsEventPayload = (isUpdateModal: boolean, source?: string): AnalyticsEventPayload => {
	try {
		return {
			action: 'shown',
			actionSubject: isUpdateModal
				? 'aiSearchBookmarkOpenUpdateModal'
				: 'aiSearchBookmarkOpenAddModal',
			eventType: 'track',
			source,
		};
	} catch (error) {
		logger.error({ error, functionName: 'onAddPinModalOpen' });
		return {};
	}
};

export const onAddPin: (
	nonPrivacySafeAttributes: {
		title: string;
		url: string;
		keyPhrases: string[];
		description: string | undefined;
	},
	source: string,
) => AnalyticsEventPayload = (
	nonPrivacySafeAttributes: {
		title: string;
		url: string;
		keyPhrases: string[];
		description: string | undefined;
	},
	source: string,
): AnalyticsEventPayload => {
	try {
		return {
			action: 'clicked',
			actionSubject: 'aiSearchBookmarkAddButton',
			eventType: 'ui',
			nonPrivacySafeAttributes,
			source,
		};
	} catch (error) {
		logger.error({ error, functionName: 'onAddPin' });
		return {};
	}
};

export const onUpdatePin: (
	nonPrivacySafeAttributes: {
		title: string;
		url: string;
		keyPhrases: string[];
		description: string | undefined;
	},
	source: string,
) => AnalyticsEventPayload = (
	nonPrivacySafeAttributes: {
		title: string;
		url: string;
		keyPhrases: string[];
		description: string | undefined;
	},
	source: string,
): AnalyticsEventPayload => {
	try {
		return {
			action: 'clicked',
			actionSubject: 'aiSearchBookmarkUpdateButton',
			eventType: 'ui',
			nonPrivacySafeAttributes,
			source,
		};
	} catch (error) {
		logger.error({ error, functionName: 'onUpdatePin' });
		return {};
	}
};

export const onLearnMoreAboutPinsClick: () => AnalyticsEventPayload = (): AnalyticsEventPayload => {
	try {
		return {
			action: 'clicked',
			actionSubject: 'aiSearchBookmarkAddLearnMore',
			eventType: 'ui',
		};
	} catch (error) {
		logger.error({ error, functionName: 'onLearnMoreAboutPinsClick' });
		return {};
	}
};

export const onAddPinDismissClick: () => AnalyticsEventPayload = (): AnalyticsEventPayload => {
	try {
		return {
			action: 'clicked',
			actionSubject: 'aiSearchBookmarkAddDismiss',
			eventType: 'ui',
		};
	} catch (error) {
		logger.error({ error, functionName: 'onAddPinDismissClick' });
		return {};
	}
};

export const onBookmarkFetchFailed: () => AnalyticsEventPayload = (): AnalyticsEventPayload => {
	try {
		return {
			action: 'failed',
			actionSubject: 'aiSearchBookmarkAddDismiss',
			eventType: 'track',
		};
	} catch (error) {
		logger.error({ error, functionName: 'onBookmarkFetchFailed' });
		return {};
	}
};

export const onBookmarkAddFailed: (isUpdateModal: boolean) => AnalyticsEventPayload = (
	isUpdateModal: boolean,
): AnalyticsEventPayload => {
	try {
		return {
			action: 'failed',
			actionSubject: isUpdateModal ? 'aiSearchBookmarkUpdate' : 'aiSearchBookmarkAddDismiss',
			eventType: 'track',
		};
	} catch (error) {
		logger.error({ error, functionName: 'onBookmarkAddFailed' });
		return {};
	}
};

export const onBookmarkLikeButton: (props: commonAttributesType) => AnalyticsEventPayload = (
	props: commonAttributesType,
): AnalyticsEventPayload => {
	try {
		return {
			action: 'clicked',
			actionSubject: 'aiSearchBookmarkLikeButton',
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
			},
			nonPrivacySafeAttributes: {
				query: props.query,
			},
		};
	} catch (error) {
		logger.error({ error, functionName: 'onBookmarkLikeButton' });
		return {};
	}
};
export const onBookmarkDislikeButton: (props: commonAttributesType) => AnalyticsEventPayload = (
	props: commonAttributesType,
): AnalyticsEventPayload => {
	try {
		return {
			action: 'clicked',
			actionSubject: 'aiSearchBookmarkDislikeButton',
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
			},
			nonPrivacySafeAttributes: {
				query: props.query,
			},
		};
	} catch (error) {
		logger.error({ error, functionName: 'onBookmarkDislikeButton' });
		return {};
	}
};

export const onPopularSearchQuerySuggestionShown: () =>
	| {
			action: string;
			actionSubject: string;
			eventType: string;
	  }
	| {
			action?: undefined;
			actionSubject?: undefined;
			eventType?: undefined;
	  } = () => {
	try {
		return {
			action: 'shown',
			actionSubject: 'popularSearchQuerySuggestion',
			eventType: 'track',
		};
	} catch (error) {
		logger.error({ error, functionName: 'onPopularSearchQuerySuggestionShown' });
		return {};
	}
};

export const onPopularSearchQueryButtonClicked: () =>
	| {
			action: string;
			actionSubject: string;
			eventType: string;
	  }
	| {
			action?: undefined;
			actionSubject?: undefined;
			eventType?: undefined;
	  } = () => {
	try {
		return {
			action: 'clicked',
			actionSubject: 'popularSearchQueryButton',
			eventType: 'ui',
		};
	} catch (error) {
		logger.error({ error, functionName: 'onPopularSearchQueryButtonClicked' });
		return {};
	}
};

export const onBookmarkRequestsCompleted = (props: {
	searchSessionId: string;
	queryHash: string;
	source: string;
	bookmarkAPIHasBookmark: boolean;
	queryIntentHasBookmark: boolean;
	bookmarkAPIBookmarkId: string | undefined;
	queryIntentBookmarkId: string | undefined;
	bookmarkAPIRequestId: string | null;
	queryIntentRequestId: string | null;
	bookmarksAreEqual: boolean;
}): AnalyticsEventPayload => {
	const {
		searchSessionId,
		queryHash,
		source,
		bookmarkAPIHasBookmark,
		queryIntentHasBookmark,
		bookmarkAPIBookmarkId,
		queryIntentBookmarkId,
		bookmarkAPIRequestId,
		queryIntentRequestId,
		bookmarksAreEqual,
	} = props;
	return {
		action: 'completed',
		actionSubject: 'aiSearchBookmarkRequests',
		eventType: 'operational',
		source,
		attributes: {
			searchSessionId,
			queryHash,
			bookmarkAPIHasBookmark,
			queryIntentHasBookmark,
			bookmarkAPIBookmarkId,
			queryIntentBookmarkId,
			bookmarkAPIRequestId,
			queryIntentRequestId,
			bookmarksAreEqual,
		},
	};
};
